import sodium from "libsodium-wrappers-sumo";

/**
 * JCS (RFC 8785) canonical JSON serialization.
 * Same implementation as did.ts — duplicated here to avoid circular deps.
 */
function jcsCanonical(value: unknown): string | undefined {
  if (value === undefined) return undefined;
  if (value === null) return "null";
  if (typeof value === "boolean" || typeof value === "number") return JSON.stringify(value);
  if (typeof value === "string") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return "[" + value.map(jcsCanonical).join(",") + "]";
  }
  if (typeof value === "object") {
    const keys = Object.keys(value as Record<string, unknown>).sort();
    const entries = keys
      .map((k) => {
        const v = jcsCanonical((value as Record<string, unknown>)[k]);
        if (v === undefined) return undefined;
        return JSON.stringify(k) + ":" + v;
      })
      .filter((entry): entry is string => entry !== undefined);
    return "{" + entries.join(",") + "}";
  }
  return JSON.stringify(value);
}

function hexToBytes(hex: string): Uint8Array {
  const h = hex.startsWith("0x") ? hex.slice(2) : hex;
  const bytes = new Uint8Array(h.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(h.substr(i * 2, 2), 16);
  }
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Compute SHA-256 hash of a JCS-canonicalized DID document.
 *
 * Uses libsodium's crypto_hash_sha256 for React Native compatibility.
 *
 * @returns 0x-prefixed hex string (66 chars).
 */
export function computeDidHash(didDocument: object): string {
  const json = jcsCanonical(didDocument) ?? "";
  const bytes = sodium.from_string(json);
  const hash = sodium.crypto_hash_sha256(bytes);
  return "0x" + bytesToHex(hash);
}

/** A single sibling in a Merkle proof path. */
export interface MerkleProofSibling {
  hash: string;
  position: "left" | "right";
}

/**
 * Verify a Merkle proof for a leaf hash against a root.
 *
 * @param leafHash - 0x-prefixed hex hash of the leaf.
 * @param proof - Array of sibling hashes with positions.
 * @param merkleRoot - 0x-prefixed hex hash of the expected root.
 * @returns true if the proof verifies.
 */
export function verifyMerkleProof(
  leafHash: string,
  proof: MerkleProofSibling[],
  merkleRoot: string,
): boolean {
  let current = hexToBytes(leafHash);

  for (const step of proof) {
    const sibling = hexToBytes(step.hash);
    const combined = new Uint8Array(current.length + sibling.length);
    if (step.position === "left") {
      combined.set(sibling);
      combined.set(current, sibling.length);
    } else {
      combined.set(current);
      combined.set(sibling, current.length);
    }
    current = sodium.crypto_hash_sha256(combined);
  }

  const rootBytes = hexToBytes(merkleRoot);
  if (current.length !== rootBytes.length) return false;
  for (let i = 0; i < current.length; i++) {
    if (current[i] !== rootBytes[i]) return false;
  }
  return true;
}
